import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { required } from '../../decorators/required';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './logo.scss?inline';

/**
 * @summary This component handles all variants of the Principal logo
 */
@customElement('pds-logo', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsLogo extends PdsElement {
  /**
   * Style variant
   * - **default** logo as it should display on light backgrounds
   * - **white** default logo with white fill
   * - **p** "P" logo as it should display on light backgrounds
   * - **p-gradient** "P" gradient logo as it should display on light backgrounds
   * - **p-white** "P" logo with a white fill, for darker backgrounds
   */
  @required
  @property()
  variant:
    | 'default'
    | 'white'
    | 'default-p-white-font'
    | 'p-gradient'
    | 'p'
    | 'p-white' = 'default';

  /**
   * @internal
   */
  @property()
  fullLogoVariants: (typeof this.variant)[] = [
    'default',
    'white',
    'default-p-white-font',
  ];

  /**
   * @internal
   */
  @property()
  pLogoVariants: (typeof this.variant)[] = ['p-gradient', 'p', 'p-white'];

  /**
   * @internal
   */
  @property()
  pLogoFills: { name: string; fill: string }[] = [
    { name: 'p-gradient', fill: 'url(#SVG_ID_GRADIENT_P_SYMBOL_1_)' },
    { name: 'p', fill: '#0091da' },
    { name: 'p-white', fill: '#ffffff' },
  ];

  /**
   * @internal
   */
  fullLogoColorMarkup() {
    switch (this.variant) {
      case 'default-p-white-font':
        return html`<style xmlns="http://www.w3.org/2000/svg" type="text/css">
          .st0 {
            fill: #ffffff;
          }
          .st1 {
            fill: url(#SVGID_1_);
          }
        </style>`;
      case 'white':
        return html`<style type="text/css">
          .st0 {
            fill: #ffffff;
          }
          .st1 {
            fill: #ffffff;
          }
        </style>`;
      default:
        return html`<style xmlns="http://www.w3.org/2000/svg" type="text/css">
          .st0 {
            fill: #4e4f53;
          }
          .st1 {
            fill: url(#SVGID_1_);
          }
        </style>`;
    }
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      /* This is equivalent to doing
       * 'primary': this.variant === 'primary',
       * 'secondary': this.variant === 'secondary',
       */
      [this.variant]: !!this.variant,
    };
  }

  render() {
    return this.fullLogoVariants.includes(this.variant)
      ? html`<span class="${this.getClass()}">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            version="1.1"
            id="Layer_1"
            x="0px"
            y="0px"
            viewBox="0 0 300 120"
            style="enable-background:new 0 0 300 120;"
            xml:space="preserve"
          >
            ${this.fullLogoColorMarkup()}
            <g>
              <path
                class="st0"
                d="M241.22,61.31c0,3.61-2.13,5.74-5.64,5.74c-2.59,0-4.16-1.39-4.16-3.61c0-2.5,2.04-3.98,5.45-3.98   c1.67,0,2.96,0.19,4.35,0.47V61.31L241.22,61.31z M241.22,56.78c-1.39-0.37-3.23-0.55-4.9-0.55c-5.92,0-9.43,2.86-9.43,7.58   c0,4.17,3.33,6.94,8.14,6.94c2.87,0,5.18-1.11,6.75-3.05c0.65,1.95,2.31,3.05,4.53,3.05c0.93,0,1.94-0.19,2.59-0.47l-0.46-3.51   c-0.37,0.09-0.83,0.19-1.2,0.19c-1.11,0-1.58-0.56-1.58-1.66v-9.71c0-5.18-3.42-8.32-9.07-8.32c-2.77,0-5.92,0.74-8.6,2.03   l1.39,3.61c1.94-1.11,4.26-1.76,6.48-1.76c3.51,0,5.36,1.57,5.36,4.62V56.78L241.22,56.78z M197.38,70.28v-6.16V47.81h-4.53v22.47   H197.38L197.38,70.28z M195.16,44.58c1.66,0,2.87-1.2,2.87-2.87c0-1.66-1.2-2.86-2.87-2.86s-2.96,1.2-2.96,2.86   C192.21,43.37,193.5,44.58,195.16,44.58L195.16,44.58L195.16,44.58z M258.23,66.86c-1.57,0-2.12-0.64-2.12-2.4V38.28h-4.53v26.82   c0,3.61,2.04,5.64,5.83,5.64c0.92,0,1.94-0.19,2.59-0.47l-0.46-3.6C259.15,66.77,258.6,66.86,258.23,66.86L258.23,66.86   L258.23,66.86z M181.11,51.32c1.85,0,3.98,0.84,5.74,2.13l2.03-3.33c-2.03-1.76-5.08-2.87-7.95-2.87c-6.57,0-11.37,4.9-11.37,11.74   c0,7.12,4.44,11.75,11.1,11.75c3.15,0,6.38-1.11,8.23-2.87l-2.03-3.24c-1.67,1.38-3.51,2.04-5.55,2.04c-4.34,0-7.12-2.96-7.12-7.68   C174.17,54.38,176.95,51.32,181.11,51.32L181.11,51.32L181.11,51.32z M157.53,47.26c-2.96,0-5.55,1.29-7.21,3.42l-0.37-2.87h-3.8   v22.47h4.53V57.71c0-3.79,2.4-6.38,5.83-6.38c3.05,0,4.99,1.95,4.99,5v13.96h4.53V55.76C166.04,50.68,162.62,47.26,157.53,47.26   L157.53,47.26L157.53,47.26z M140.23,70.28V63.9V47.81h-4.53v22.47H140.23L140.23,70.28z M138.02,44.58c1.66,0,2.87-1.2,2.87-2.87   c0-1.66-1.2-2.86-2.87-2.86c-1.67,0-2.96,1.2-2.96,2.86C135.06,43.37,136.35,44.58,138.02,44.58L138.02,44.58L138.02,44.58z    M129.14,47.44c-2.59,0-4.99,1.2-6.29,3.24l-0.37-2.87h-3.79v22.47h4.53V58.17c0-4.35,2.03-6.66,5.92-6.66   c0.65,0,1.57,0.18,2.5,0.36l0.55-3.88C131.27,47.62,130.25,47.44,129.14,47.44L129.14,47.44L129.14,47.44z M98.44,43.65h5.64   c4.25,0,6.75,2.12,6.75,5.92c0,3.79-2.5,5.92-6.75,5.92h-5.64V43.65L98.44,43.65z M93.72,39.68v30.61h4.72V59.46h6.29   c6.47,0,10.82-3.97,10.82-9.89c0-5.92-4.35-9.89-10.82-9.89H93.72L93.72,39.68z M213.1,66.68c-2.03,0-4.25-1.02-5.36-2.4v-6.76   c0-3.79,2.31-6.2,5.82-6.2c3.7,0,6.01,2.78,6.01,7.22C219.58,63.72,217.17,66.68,213.1,66.68L213.1,66.68L213.1,66.68z    M214.49,47.26c-2.87,0-5.55,1.2-7.21,3.42l-0.37-2.87h-3.79v31.53h4.53V68.53c1.38,1.29,3.97,2.22,6.19,2.22   c6.2,0,10.36-4.99,10.36-12.21C224.2,51.79,220.32,47.26,214.49,47.26L214.49,47.26L214.49,47.26z"
              />
              <path
                class="st0"
                d="M264.85,42.55h0.99c0.5,0,0.84-0.3,0.84-0.77c0-0.48-0.34-0.79-0.84-0.79h-0.99V42.55L264.85,42.55z    M263.73,40.07h2.26c1.06,0,1.82,0.68,1.82,1.66c0,0.78-0.45,1.36-1.13,1.52l1.3,2.01h-1.27l-1.11-1.84h-0.75v1.84h-1.12V40.07   L263.73,40.07z M265.68,46.43c2.21,0,3.76-1.55,3.76-3.76c0-2.21-1.55-3.76-3.76-3.76c-2.21,0-3.76,1.55-3.76,3.76   C261.92,44.88,263.47,46.43,265.68,46.43L265.68,46.43z M265.68,38.08c2.7,0,4.6,1.9,4.6,4.6c0,2.7-1.9,4.6-4.6,4.6   s-4.6-1.9-4.6-4.6C261.08,39.97,262.98,38.08,265.68,38.08L265.68,38.08z"
              />
              <linearGradient
                id="SVGID_1_"
                gradientUnits="userSpaceOnUse"
                x1="33.1678"
                y1="79.4673"
                x2="78.2508"
                y2="34.3843"
              >
                <stop offset="0" style="stop-color:#0076CF" />
                <stop offset="0.1183" style="stop-color:#0080D3" />
                <stop offset="0.25" style="stop-color:#0091DA" />
                <stop offset="0.6447" style="stop-color:#00A7CF" />
                <stop offset="0.75" style="stop-color:#00AECB" />
                <stop offset="1" style="stop-color:#00C4D9" />
              </linearGradient>
              <path
                class="st1"
                d="M83.06,48.94c0-13.56-10.59-23.5-25.08-23.5c-17.84,0-31.31,13.94-31.31,32.24   c0,19.14,14.68,35.12,33.73,36.89V70.22c-8.92-0.83-15.05-7.43-15.05-16.16c0-8.46,5.66-14.59,13.47-14.59   c7.15,0,11.98,4.46,11.98,11.06c0,5.85-3.71,9.66-10.4,10.69v9.01C74.14,69.85,83.06,61.49,83.06,48.94L83.06,48.94z"
              />
            </g></svg
        ></span>`
      : html`<svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          version="1.1"
          x="0px"
          y="0px"
          viewBox="0 0 154 187"
          style="enable-background:new 0 0 154 187;"
          xml:space="preserve"
        >
          <style type="text/css">
            .logoPSymbol {
              fill: ${this.pLogoFills.find((fill) => fill.name === this.variant)
                ?.fill};
            }
          </style>
          <linearGradient
            id="SVG_ID_GRADIENT_P_SYMBOL_1_"
            gradientUnits="userSpaceOnUse"
            x1="28.6564"
            y1="157.3228"
            x2="114.4336"
            y2="8.7523"
          >
            <stop offset="0" style="stop-color:#0068A6" />
            <stop offset="0.5" style="stop-color:#0091DA" />
            <stop offset="1" style="stop-color:#00C4D9" />
          </linearGradient>
          <path
            class="logoPSymbol"
            d="M151.1,64.9c0-35.3-27.6-61.2-65.3-61.2C39.3,3.7,4.2,40,4.2,87.6c0,49.8,38.2,91.4,87.8,96v-63.4    c-23.2-2.2-39.2-19.3-39.2-42.1c0-22,14.8-38,35.1-38c18.6,0,31.2,11.6,31.2,28.8c0,15.2-9.7,25.2-27.1,27.8v23.5    C127.9,119.3,151.1,97.6,151.1,64.9z"
          />
        </svg>`;
  }
}
